import { useMemo } from "react"

import { useDraggable } from "shared/lib/drag-and-drop/useDraggable"
import { useDroppable } from "shared/lib/drag-and-drop/useDroppable"
import { useMergedRefs } from "shared/lib/useMergedRefs"

import { useTabBarContext } from "../context/TabBarContext"

interface UseTabBarTabContainerDraggableViewModelProps {
  eventKey: string
  index: number
  label: string
  isDragDisabled?: boolean
  dragSourceData?: unknown
  disabled?: boolean
}

export function useTabBarTabContainerDraggableViewModel({
  eventKey,
  index,
  label,
  isDragDisabled,
  dragSourceData,
  disabled
}: UseTabBarTabContainerDraggableViewModelProps) {
  const { enableSort, hoverIndex, isSelected } = useTabBarContext()

  const sortEnabled = !!enableSort && !disabled

  const {
    attributes,
    listeners,
    setNodeRef: setDragNodeRef,
    isDragging
  } = useDraggable({
    id: `tab-drag-${eventKey}`,
    data: dragSourceData,
    meta: { index, eventKey },
    type: "tab-bar-tab",
    label,
    disabled: !sortEnabled || isDragDisabled
  })

  const { setNodeRef: setDropNodeRef } = useDroppable({
    id: `tab-drop-${eventKey}`,
    data: dragSourceData,
    meta: { index, eventKey },
    type: "tab-bar-tab",
    accept: ["tab-bar-tab"],
    disabled: !sortEnabled || isDragDisabled,
    sameOrigin: true
  })

  const insertBefore = index !== -1 && index === 0 && hoverIndex === index
  const insertAfter = index !== -1 && index + 1 === hoverIndex

  const dndRef = useMergedRefs(setDragNodeRef, setDropNodeRef)

  const selected = useMemo(() => isSelected(eventKey), [isSelected, eventKey])

  return useMemo(
    () => ({
      dndRef,
      dndAttributes: attributes,
      dndListeners: listeners,
      isDragging,
      insertBefore: !!insertBefore,
      insertAfter: !!insertAfter,
      selected
    }),
    [
      dndRef,
      selected,
      attributes,
      listeners,
      isDragging,
      insertBefore,
      insertAfter
    ]
  )
}
